// src/components/category/SimilarNamesEditor.tsx
import React, { useState } from "react";

interface SimilarNamesEditorProps {
    /** Current similar names (aliases/synonyms). */
    values: string[];
    /** Add a name. May be async (e.g. a PATCH). */
    onAdd: (name: string) => void | Promise<void>;
    /** Remove a name. May be async. */
    onRemove: (name: string) => void | Promise<void>;
    disabled?: boolean;
}

/**
 * Chip-style editor for a category's "similar names" (aliases). Type a name and
 * press Enter or Add; click × on a chip to remove it. Purely presentational —
 * the parent decides whether add/remove hits the API or a local list.
 */
const SimilarNamesEditor: React.FC<SimilarNamesEditorProps> = ({
    values,
    onAdd,
    onRemove,
    disabled = false,
}) => {
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        const name = draft.trim();
        if (!name) return;
        // Case-insensitive duplicate guard (server dedups too).
        if (values.some(v => v.toLowerCase() === name.toLowerCase())) {
            setDraft("");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await onAdd(name);
            setDraft("");
        } catch (e: any) {
            setError(e?.message ?? "Could not add");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (name: string) => {
        setBusy(true);
        setError(null);
        try {
            await onRemove(name);
        } catch (e: any) {
            setError(e?.message ?? "Could not remove");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            {values.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                    {values.map(v => (
                        <span
                            key={v}
                            style={{
                                display: "inline-flex", alignItems: "center", gap: "0.35rem",
                                background: "var(--surface-2, #eef2f7)", borderRadius: "999px",
                                padding: "0.15rem 0.6rem", fontSize: "0.85rem",
                            }}
                        >
                            {v}
                            <button
                                type="button"
                                onClick={() => remove(v)}
                                disabled={disabled || busy}
                                aria-label={`Remove ${v}`}
                                style={{
                                    border: "none", background: "transparent", cursor: "pointer",
                                    fontSize: "1rem", lineHeight: 1, padding: 0, color: "var(--text-secondary)",
                                }}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                <input
                    className="parameter-editor-input"
                    type="text"
                    placeholder="Add a similar name…"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            submit();
                        }
                    }}
                    disabled={disabled || busy}
                    style={{ flex: 1 }}
                />
                <button
                    type="button"
                    className="btn-sm"
                    onClick={submit}
                    disabled={disabled || busy || !draft.trim()}
                >
                    Add
                </button>
            </div>

            {error && (
                <div className="error-message" style={{ fontSize: "0.82rem" }}>{error}</div>
            )}
        </div>
    );
};

export default SimilarNamesEditor;
